// screens/AudienceInbox.tsx — Chunk AUD-W, plates 04/05: the Audience
// tab's inbox. The list and its state filter (plate 04) render here; the
// one-item spread — the message, the reply box, Draft/Send, Promote, Not a
// customer, Hide (plate 05) — renders in AudienceInboxDetail below, a
// separate route (`#/audience/inbox/:id`) rather than a split pane, the
// same convention Threads/ThreadSpread already hold in this codebase.
//
// Replies never send themselves (the rule that binds this whole tab): Draft
// only ever fills the box, never the wire; Send is its own button, its own
// press.
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  draftAudienceReply,
  getAudienceInboxItem,
  hideAudienceItem,
  listAudienceInbox,
  markAudienceNotCustomer,
  promoteAudienceItem,
  reopenAudienceItem,
  sendAudienceReply,
  unpromoteAudienceItem,
  type AudienceInboxCountsApi,
  type AudienceInboxFilter,
  type SocialItemApi,
} from '../lib/api';
import './AudienceInbox.css';

const FILTERS: Array<{ id: AudienceInboxFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'needs_reply', label: 'Needs reply' },
  { id: 'answered', label: 'Answered' },
  { id: 'promoted', label: 'Promoted' },
  { id: 'hidden', label: 'Hidden' },
  { id: 'not_customer', label: 'Not a customer' },
];

function countFor(counts: AudienceInboxCountsApi | null, id: AudienceInboxFilter): number | null {
  if (!counts) return null;
  if (id === 'all') return counts.needs_reply + counts.answered + counts.promoted + counts.hidden + counts.not_customer;
  return counts[id];
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60_000))}m`;
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function clockLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export interface AudienceInboxProps {
  tenantId: string;
  filter: AudienceInboxFilter;
  onSelectFilter: (f: AudienceInboxFilter) => void;
  onOpen: (id: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; items: SocialItemApi[]; counts: AudienceInboxCountsApi };

export function AudienceInbox({ tenantId, filter, onSelectFilter, onOpen }: AudienceInboxProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setLoad({ phase: 'loading' });
    listAudienceInbox(tenantId, filter)
      .then(({ items, counts }) => {
        if (!cancelled) setLoad({ phase: 'ready', items, counts });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the inbox could not be read' });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, filter]);

  const counts = load.phase === 'ready' ? load.counts : null;

  return (
    <div class="aud-inbox">
      <nav class="aud-inbox__filters" aria-label="Filter the inbox by state">
        {FILTERS.map((f) => {
          const n = countFor(counts, f.id);
          return (
            <button
              type="button"
              key={f.id}
              class={`aud-inbox__filter${f.id === filter ? ' is-active' : ''}`}
              onClick={() => onSelectFilter(f.id)}
            >
              {f.label}
              {n !== null && n > 0 ? <span class="aud-inbox__filter-count">{n}</span> : null}
            </button>
          );
        })}
      </nav>

      {load.phase === 'loading' ? (
        <div class="aud-inbox__skeleton" aria-hidden="true">
          <div class="aud-inbox__skeleton-row" />
          <div class="aud-inbox__skeleton-row" />
          <div class="aud-inbox__skeleton-row" />
        </div>
      ) : load.phase === 'error' ? (
        <div class="aud-inbox__say">
          The inbox could not be read.
          <br />
          <span class="aud-inbox__say-soft">{load.message}</span>
        </div>
      ) : load.items.length === 0 ? (
        <div class="aud-inbox__say">
          {filter === 'all' ? 'Nothing in the inbox yet.' : `Nothing ${FILTERS.find((f) => f.id === filter)?.label.toLowerCase()}.`}
          <br />
          <span class="aud-inbox__say-soft">
            Comments and messages on a connected account land here as they arrive.
          </span>
        </div>
      ) : (
        <div class="aud-inbox__list">
          {load.items.map((item) => (
            <button type="button" class="aud-inbox__row" key={item.id} onClick={() => onOpen(item.id)}>
              <span class="aud-inbox__row-main">
                <span class="aud-inbox__row-author">{item.authorName || item.authorUsername}</span>
                <span class="aud-inbox__row-body">{item.body}</span>
              </span>
              <span class="aud-inbox__row-meta">
                <span class="aud-inbox__row-platform">
                  {item.platform} · {item.kind}
                </span>
                <span class="aud-inbox__row-time">{relativeTime(item.receivedAt)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * AudienceInboxDetail — plate 05. One item: the message, a reply box,
 * Draft (fills the box, never sends), Send reply, Promote to customer
 * (opens a card asking "This does / It does not"), Not a customer, Hide.
 * A promote leaves a receipt with Undo (the 10-minute window the server
 * itself enforces — a 409 past it shows the server's own sentence).
 * ------------------------------------------------------------------ */

export interface AudienceInboxDetailProps {
  tenantId: string;
  id: string;
  backLabel: string;
  onBack: () => void;
  onOpenThread: (threadId: string) => void;
}

type DetailLoad =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; item: SocialItemApi };

type PromoteCard = { open: boolean; displayName: string; threadSubject: string };

export function AudienceInboxDetail({ tenantId, id, backLabel, onBack, onOpenThread }: AudienceInboxDetailProps) {
  const [load, setLoad] = useState<DetailLoad>({ phase: 'loading' });
  const [replyText, setReplyText] = useState('');
  const [busy, setBusy] = useState(false);
  const [draftRefusal, setDraftRefusal] = useState<string | null>(null);
  const [actionRefusal, setActionRefusal] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  const [promoteCard, setPromoteCard] = useState<PromoteCard>({ open: false, displayName: '', threadSubject: '' });

  useEffect(() => {
    let cancelled = false;
    setLoad({ phase: 'loading' });
    setReplyText('');
    setReceipt(null);
    setActionRefusal(null);
    setDraftRefusal(null);
    getAudienceInboxItem(tenantId, id)
      .then((item) => {
        if (cancelled) return;
        setLoad({ phase: 'ready', item });
        setReplyText(item.draftReply ?? '');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'this item could not be read' });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, id]);

  if (load.phase === 'loading') {
    return <div class="aud-detail" aria-hidden="true" />;
  }
  if (load.phase === 'error') {
    return (
      <div class="aud-detail">
        <BackLink label={backLabel} onBack={onBack} />
        <div class="aud-detail__say">
          This item could not be read.
          <br />
          <span class="aud-detail__say-soft">{load.message}</span>
        </div>
      </div>
    );
  }

  const item = load.item;

  async function draft(): Promise<void> {
    setBusy(true);
    setDraftRefusal(null);
    try {
      const d = await draftAudienceReply(tenantId, id);
      setReplyText(d);
    } catch (err) {
      setDraftRefusal(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'the draft could not be written');
    } finally {
      setBusy(false);
    }
  }

  async function send(): Promise<void> {
    if (!replyText.trim()) return;
    setBusy(true);
    setActionRefusal(null);
    try {
      const { item: updated, receipt: r } = await sendAudienceReply(tenantId, id, replyText);
      setLoad({ phase: 'ready', item: updated });
      setReceipt(r);
    } catch (err) {
      setActionRefusal(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'the reply could not be sent');
    } finally {
      setBusy(false);
    }
  }

  async function hide(): Promise<void> {
    setBusy(true);
    setActionRefusal(null);
    try {
      const { item: updated, receipt: r } = await hideAudienceItem(tenantId, id);
      setLoad({ phase: 'ready', item: updated });
      setReceipt(r);
    } catch (err) {
      setActionRefusal(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'this could not be hidden');
    } finally {
      setBusy(false);
    }
  }

  async function notCustomer(): Promise<void> {
    setBusy(true);
    setActionRefusal(null);
    try {
      const { item: updated, receipt: r } = await markAudienceNotCustomer(tenantId, id);
      setLoad({ phase: 'ready', item: updated });
      setReceipt(r);
    } catch (err) {
      setActionRefusal(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'this could not be marked');
    } finally {
      setBusy(false);
    }
  }

  async function reopen(): Promise<void> {
    setBusy(true);
    setActionRefusal(null);
    try {
      const { item: updated } = await reopenAudienceItem(tenantId, id);
      setLoad({ phase: 'ready', item: updated });
      setReceipt(null);
    } catch (err) {
      setActionRefusal(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'this could not be reopened');
    } finally {
      setBusy(false);
    }
  }

  async function promote(): Promise<void> {
    setBusy(true);
    setActionRefusal(null);
    try {
      const { item: updated, threadId, receipt: r } = await promoteAudienceItem(tenantId, id, {
        displayName: promoteCard.displayName.trim() || undefined,
        threadSubject: promoteCard.threadSubject.trim() || undefined,
      });
      setLoad({ phase: 'ready', item: updated });
      setReceipt(r);
      setPromoteCard({ open: false, displayName: '', threadSubject: '' });
      (updated as SocialItemApi & { _threadId?: string })._threadId = threadId;
    } catch (err) {
      setActionRefusal(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'this could not be promoted');
    } finally {
      setBusy(false);
    }
  }

  async function undoPromote(): Promise<void> {
    setBusy(true);
    setActionRefusal(null);
    try {
      const { item: updated, receipt: r } = await unpromoteAudienceItem(tenantId, id);
      setLoad({ phase: 'ready', item: updated });
      setReceipt(r);
    } catch (err) {
      // Past the 10-minute window the server's own 409 sentence shows here,
      // verbatim — never a second, vaguer copy.
      setActionRefusal(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'this could not be undone');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="aud-detail">
      <BackLink label={backLabel} onBack={onBack} />

      <div class="aud-detail__who">
        <span class="aud-detail__author">{item.authorName || item.authorUsername}</span>
        <span class="aud-detail__handle">@{item.authorUsername}</span>
        <span class="aud-detail__meta">
          {item.platform} · {item.kind} · {clockLabel(item.receivedAt)}
        </span>
      </div>
      {item.postTitle ? (
        <div class="aud-detail__post">
          on &ldquo;{item.postTitle}&rdquo;
          {item.postPermalink ? (
            <>
              {' · '}
              <a href={item.postPermalink} target="_blank" rel="noreferrer">
                open
              </a>
            </>
          ) : null}
        </div>
      ) : null}

      <div class="aud-detail__body">{item.body}</div>

      <div class="aud-detail__state">{stateLabel(item.state)}</div>

      {item.state === 'promoted' ? (
        <div class="aud-detail__promoted">
          Promoted to a customer{item.promotedAt ? ` · ${clockLabel(item.promotedAt)}` : ''}.
          {item.promotedThreadId ? (
            <button type="button" class="aud-detail__link" onClick={() => onOpenThread(item.promotedThreadId!)}>
              Open the conversation
            </button>
          ) : null}
          <button type="button" class="aud-detail__btn" disabled={busy} onClick={() => void undoPromote()}>
            Undo
          </button>
        </div>
      ) : item.repliedAt ? (
        <div class="aud-detail__replied">
          Replied on {item.platform} · {clockLabel(item.repliedAt)}
          {item.replyText ? <div class="aud-detail__reply-sent">{item.replyText}</div> : null}
        </div>
      ) : null}

      {receipt ? <div class="aud-detail__receipt">{receipt}</div> : null}
      {actionRefusal ? <div class="aud-detail__refusal">{actionRefusal}</div> : null}

      {(item.state === 'needs_reply' || item.state === 'answered') && (
        <div class="aud-detail__reply">
          <textarea
            class="aud-detail__textarea"
            placeholder="Write a reply"
            value={replyText}
            onInput={(e) => setReplyText((e.target as HTMLTextAreaElement).value)}
            rows={4}
          />
          {draftRefusal ? <div class="aud-detail__refusal">{draftRefusal}</div> : null}
          <div class="aud-detail__actions">
            <button type="button" class="aud-detail__btn" disabled={busy} onClick={() => void draft()}>
              Draft
            </button>
            <button
              type="button"
              class="aud-detail__btn aud-detail__btn--primary"
              disabled={busy || !replyText.trim()}
              onClick={() => void send()}
            >
              Send reply
            </button>
          </div>
        </div>
      )}

      {item.state === 'needs_reply' && (
        <div class="aud-detail__actions">
          {promoteCard.open ? (
            <div class="aud-detail__promote-card">
              <p class="aud-detail__promote-ask">Make {item.authorName || `@${item.authorUsername}`} a customer?</p>
              <div class="aud-detail__promote-terms">
                <p>
                  <strong>This does:</strong> adds them as a contact, with their {item.platform} kept on the
                  record, and opens a conversation for them in Conversations. Their next messages land there.
                </p>
                <p>
                  <strong>It does not:</strong> send them anything, quote a price, or add them to the email list.
                </p>
              </div>
              <label class="aud-detail__field">
                Name (optional)
                <input
                  type="text"
                  value={promoteCard.displayName}
                  onInput={(e) => setPromoteCard((c) => ({ ...c, displayName: (e.target as HTMLInputElement).value }))}
                />
              </label>
              <label class="aud-detail__field">
                Conversation subject (optional)
                <input
                  type="text"
                  value={promoteCard.threadSubject}
                  onInput={(e) => setPromoteCard((c) => ({ ...c, threadSubject: (e.target as HTMLInputElement).value }))}
                />
              </label>
              <div class="aud-detail__actions">
                <button type="button" class="aud-detail__btn aud-detail__btn--primary" disabled={busy} onClick={() => void promote()}>
                  Promote
                </button>
                <button
                  type="button"
                  class="aud-detail__btn"
                  disabled={busy}
                  onClick={() => setPromoteCard({ open: false, displayName: '', threadSubject: '' })}
                >
                  Not now
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              class="aud-detail__btn"
              disabled={busy}
              onClick={() => setPromoteCard({ open: true, displayName: '', threadSubject: '' })}
            >
              Promote to customer
            </button>
          )}
          <button type="button" class="aud-detail__btn" disabled={busy} onClick={() => void notCustomer()}>
            Not a customer
          </button>
          <button type="button" class="aud-detail__btn" disabled={busy} onClick={() => void hide()}>
            Hide
          </button>
        </div>
      )}

      {(item.state === 'hidden' || item.state === 'not_customer') && (
        <div class="aud-detail__actions">
          <button type="button" class="aud-detail__btn" disabled={busy} onClick={() => void reopen()}>
            Reopen
          </button>
        </div>
      )}
    </div>
  );
}

function stateLabel(state: SocialItemApi['state']): string {
  switch (state) {
    case 'needs_reply':
      return 'Needs a reply';
    case 'answered':
      return 'Answered';
    case 'promoted':
      return 'Promoted';
    case 'hidden':
      return 'Hidden';
    case 'not_customer':
      return 'Not a customer';
  }
}

function BackLink({ label, onBack }: { label: string; onBack: () => void }) {
  return (
    <button type="button" class="aud-detail__back" onClick={onBack}>
      ← {label}
    </button>
  );
}
